import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Stack,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReplayIcon from "@mui/icons-material/Replay";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import {
  CampaignFollowUpJob,
  CampaignMessageDoc,
  CampaignMessageStatus,
  FollowUpAudience,
  FollowUpDelayUnit,
  ContactList,
  TemplateVariableMapping,
  cancelCampaignFollowUp,
  deleteCampaignFollowUp,
  duplicateCampaignFollowUp,
  getCampaignFollowUp,
  getCampaignFollowUpMessages,
  getLists,
  pauseCampaignFollowUp,
  resumeCampaignFollowUp,
  retryFailedCampaignFollowUp,
  retryQueuedCampaignFollowUp,
  updateCampaignFollowUp,
} from "../../services/campaign.service";
import { WhatsAppTemplate } from "../../models/whatsapp.model";
import { getAllTemplates } from "../../services/settings.service";
import useAuthUser from "../hooks/useAuthUser";
import TemplateVariableMapper, { componentsFromVariableMappings, mappingsFromTemplateConfig } from "./TemplateVariableMapper";

type Props = {
  uuid: string;
  campaignId: string;
  followUpId: string;
  campaignName: string;
  listIds: string[];
  onBack: () => void;
  onChanged: () => void;
};

const statuses: CampaignMessageStatus[] = ["QUEUED", "SENT", "DELIVERED", "READ", "FAILED"];

const toLocalInput = (value?: string | null) => {
  if (!value) return "";
  const date = new Date(value);
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

const csvCell = (value: unknown) => `"${String(value ?? "").replace(/"/g, '""')}"`;

export default function FollowUpDetails({
  uuid,
  campaignId,
  followUpId,
  campaignName,
  listIds,
  onBack,
  onChanged,
}: Props) {
  const [job, setJob] = useState<CampaignFollowUpJob | null>(null);
  const [messages, setMessages] = useState<CampaignMessageDoc[]>([]);
  const [filter, setFilter] = useState<CampaignMessageStatus | "">("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [delayAmount, setDelayAmount] = useState(1);
  const [delayUnit, setDelayUnit] = useState<FollowUpDelayUnit>("DAYS");
  const [audience, setAudience] = useState<FollowUpAudience>("ALL");
  const [templateName, setTemplateName] = useState("");
  const [language, setLanguage] = useState("en_US");
  const [variableMappings, setVariableMappings] = useState<TemplateVariableMapping[]>([]);
  const [lists, setLists] = useState<ContactList[]>([]);
  const [approvedTemplates, setApprovedTemplates] = useState<WhatsAppTemplate[]>([]);
  const [scheduledLocal, setScheduledLocal] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [nextJob, nextMessages] = await Promise.all([
        getCampaignFollowUp(uuid, campaignId, followUpId),
        getCampaignFollowUpMessages(uuid, campaignId, followUpId, filter || undefined),
      ]);
      setJob(nextJob);
      setMessages(nextMessages);
      setDelayAmount(nextJob.delayAmount);
      setDelayUnit(nextJob.delayUnit);
      setAudience(nextJob.audience);
      setTemplateName(nextJob.template?.name || "");
      setLanguage(nextJob.template?.language || "en_US");
      setVariableMappings(mappingsFromTemplateConfig(nextJob.template));
      setScheduledLocal(toLocalInput(nextJob.scheduledAt));
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load follow-up");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid, campaignId, followUpId, filter]);

  const { user } = useAuthUser();
  useEffect(() => {
    getLists(uuid).then(setLists).catch(() => setLists([]));
  }, [uuid]);
  useEffect(() => {
    if (!user?.uid) return;
    getAllTemplates(user.uid)
      .then((templates) => setApprovedTemplates((templates || []).filter((template: any) => template.status === "APPROVED")))
      .catch(() => setApprovedTemplates([]));
  }, [user?.uid]);
  const availableColumns = useMemo(
    () => Array.from(new Set(["name", "phone", ...lists.filter((list) => listIds.includes(list._id)).flatMap((list) => list.columns || [])])),
    [lists, listIds],
  );
  const selectedTemplate = approvedTemplates.find((template) => template.name === templateName) || null;
  const variableMappingsIncomplete = variableMappings.some(
    (mapping) => (mapping.source === "COLUMN" && !mapping.column) || (mapping.source === "STATIC" && !mapping.value?.trim()),
  );

  const status = String(job?.status || "").toUpperCase();
  const metrics = job?.metrics || {};
  const editable = ["WAITING", "SCHEDULED", "PAUSED", "FAILED"].includes(status);
  const queued = Number(metrics.QUEUED || 0);
  const failed = Number(metrics.FAILED || 0);

  const runAction = async (action: () => Promise<any>, confirmText?: string) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(true);
    setError("");
    try {
      await action();
      await load();
      onChanged();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Follow-up action failed");
    } finally {
      setBusy(false);
    }
  };

  const save = () => {
    return runAction(() =>
      updateCampaignFollowUp(uuid, campaignId, followUpId, {
        delayAmount,
        delayUnit,
        audience,
        template: {
          name: templateName.trim(),
          language: language.trim() || "en_US",
          components: componentsFromVariableMappings(variableMappings),
          variableMappings,
        },
        ...(scheduledLocal ? { scheduledAt: new Date(scheduledLocal).toISOString() } : {}),
      }),
    );
  };

  const exportCsv = () => {
    const rows = [
      ["Name", "Phone", "List", "Status", "WhatsApp message ID", "Error"],
      ...messages.map((message) => [
        message.contactName || "",
        message.to,
        message.listName || "",
        message.status,
        message.waMessageId || "",
        message.lastError?.message || message.error?.message || "",
      ]),
    ];
    const blob = new Blob(
      ["\uFEFF", rows.map((row) => row.map(csvCell).join(",")).join("\r\n")],
      { type: "text/csv;charset=utf-8" },
    );
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${campaignName.replace(/[^a-z0-9_-]+/gi, "-")}-follow-up-${job?.step || ""}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const removeFollowUp = async () => {
    if (!window.confirm("Delete this follow-up permanently?")) return;
    setBusy(true);
    setError("");
    try {
      await deleteCampaignFollowUp(uuid, campaignId, followUpId);
      await onChanged();
      onBack();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Could not delete follow-up");
      setBusy(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Button startIcon={<ArrowBackIcon />} onClick={onBack}>Parent campaign</Button>
            <Box>
              <Typography variant="overline">{campaignName}</Typography>
              <Typography variant="h5" fontWeight={900}>Follow-up {job?.step || ""}</Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>
                <Chip label={job?.status || "Loading"} color={status === "COMPLETED" ? "success" : "default"} />
                <Chip variant="outlined" label={job?.audience === "INTERESTED" ? "Interested only" : "All recipients"} />
              </Stack>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button startIcon={<SettingsOutlinedIcon />} onClick={() => setSettingsOpen(true)}>Settings</Button>
            {["WAITING", "SCHEDULED", "RUNNING"].includes(status) && (
              <Button startIcon={<PauseIcon />} disabled={busy} onClick={() => runAction(() => pauseCampaignFollowUp(uuid, campaignId, followUpId))}>Pause</Button>
            )}
            {status === "PAUSED" && (
              <Button startIcon={<PlayArrowIcon />} disabled={busy} onClick={() => runAction(() => resumeCampaignFollowUp(uuid, campaignId, followUpId))}>Resume</Button>
            )}
            {queued > 0 && ["FAILED", "COMPLETED", "PAUSED"].includes(status) && (
              <Button startIcon={<ReplayIcon />} disabled={busy} onClick={() => runAction(() => retryQueuedCampaignFollowUp(uuid, campaignId, followUpId))}>Retry queued ({queued})</Button>
            )}
            {failed > 0 && (
              <Button color="error" startIcon={<ReplayIcon />} disabled={busy || status !== "COMPLETED"} onClick={() => runAction(() => retryFailedCampaignFollowUp(uuid, campaignId, followUpId))}>Retry failed ({failed})</Button>
            )}
            <Button startIcon={<ContentCopyIcon />} disabled={busy} onClick={() => runAction(() => duplicateCampaignFollowUp(uuid, campaignId, followUpId))}>Duplicate</Button>
            {!['COMPLETED', 'CANCELLED'].includes(status) && (
              <Button color="warning" disabled={busy} onClick={() => runAction(() => cancelCampaignFollowUp(uuid, campaignId, followUpId), "Cancel this follow-up?")}>Cancel</Button>
            )}
            <Button color="error" startIcon={<DeleteOutlineIcon />} disabled={busy} onClick={removeFollowUp}>Delete</Button>
            <Button startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />} onClick={load} disabled={loading}>Refresh</Button>
          </Stack>
        </Stack>
      </Paper>

      {error && <Alert severity="error">{error}</Alert>}

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(5, 1fr)" }, gap: 1 }}>
        {["TOTAL", "QUEUED", "SENT", "READ", "FAILED"].map((key) => (
          <Paper key={key} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
            <Typography variant="caption">{key}</Typography>
            <Typography variant="h5" fontWeight={900}>{Number(metrics[key] || 0)}</Typography>
          </Paper>
        ))}
      </Box>

      <Dialog open={settingsOpen} onClose={() => !busy && setSettingsOpen(false)} fullWidth maxWidth="md">
        <DialogTitle fontWeight={800}>Follow-up settings</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ opacity: 0.7, mb: 2 }}>A follow-up is a child campaign and cannot contain nested follow-ups.</Typography>
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <FormControl disabled={!editable} fullWidth>
                <InputLabel>Approved template</InputLabel>
                <Select value={templateName} label="Approved template" onChange={(event) => {
                  setTemplateName(event.target.value);
                  const selected = approvedTemplates.find((template) => template.name === event.target.value);
                  if (selected?.language) setLanguage(selected.language);
                }}>
                  {approvedTemplates.map((template) => <MenuItem key={template.name} value={template.name}>{template.name}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField label="Language" value={language} onChange={(event) => setLanguage(event.target.value)} disabled={!editable} fullWidth />
            </Stack>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField type="number" label="Delay" value={delayAmount} onChange={(event) => setDelayAmount(Number(event.target.value))} disabled={!editable} fullWidth />
              <TextField select label="Delay unit" value={delayUnit} onChange={(event) => setDelayUnit(event.target.value as FollowUpDelayUnit)} disabled={!editable} fullWidth>
                <MenuItem value="MINUTES">Minutes</MenuItem><MenuItem value="HOURS">Hours</MenuItem><MenuItem value="DAYS">Days</MenuItem>
              </TextField>
              <TextField select label="Audience" value={audience} onChange={(event) => setAudience(event.target.value as FollowUpAudience)} disabled={!editable} fullWidth>
                <MenuItem value="ALL">All original recipients</MenuItem><MenuItem value="INTERESTED">Interested only</MenuItem>
              </TextField>
              <TextField type="datetime-local" label="Schedule" value={scheduledLocal} onChange={(event) => setScheduledLocal(event.target.value)} disabled={!editable} InputLabelProps={{ shrink: true }} fullWidth />
            </Stack>
            <TemplateVariableMapper template={selectedTemplate} availableColumns={availableColumns} value={variableMappings} onChange={setVariableMappings} title="Follow-up template variables" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)} disabled={busy}>Close</Button>
          {editable && <Button variant="contained" onClick={save} disabled={busy || !templateName.trim() || variableMappingsIncomplete}>Save changes</Button>}
        </DialogActions>
      </Dialog>

      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1} sx={{ p: 2 }}>
          <Typography variant="h6" fontWeight={800}>Message activity</Typography>
          <Button startIcon={<DownloadOutlinedIcon />} onClick={exportCsv} disabled={!messages.length}>Export CSV</Button>
        </Stack>
        <Divider />
        <Stack direction="row" spacing={1} sx={{ p: 1.5, flexWrap: "wrap" }}>
          <Button size="small" variant={!filter ? "contained" : "outlined"} onClick={() => setFilter("")}>All</Button>
          {statuses.map((item) => <Button key={item} size="small" variant={filter === item ? "contained" : "outlined"} onClick={() => setFilter(item)}>{item}</Button>)}
        </Stack>
        <TableContainer>
          <Table size="small">
            <TableHead><TableRow><TableCell>Contact</TableCell><TableCell>List</TableCell><TableCell>Status</TableCell><TableCell>Message ID</TableCell><TableCell>Error</TableCell></TableRow></TableHead>
            <TableBody>
              {messages.map((message) => (
                <TableRow key={message._id}>
                  <TableCell><Typography fontWeight={700}>{message.contactName || message.to}</Typography>{message.contactName && <Typography variant="caption">{message.to}</Typography>}</TableCell>
                  <TableCell>{message.listName || "-"}</TableCell><TableCell><Chip size="small" label={message.status} /></TableCell>
                  <TableCell sx={{ maxWidth: 220, wordBreak: "break-all" }}>{message.waMessageId || "-"}</TableCell>
                  <TableCell>{message.lastError?.message || message.error?.message || "-"}</TableCell>
                </TableRow>
              ))}
              {!messages.length && <TableRow><TableCell colSpan={5} align="center">No messages found.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {(job?.activity?.length || 0) > 0 && (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
          <Typography variant="h6" fontWeight={800}>Activity history</Typography>
          <Stack spacing={1} sx={{ mt: 1 }}>
            {job?.activity?.slice().reverse().map((entry, index) => (
              <Box key={`${entry.createdAt}-${index}`}><Typography variant="body2" fontWeight={700}>{entry.action.replace(/_/g, " ")}</Typography><Typography variant="caption">{new Date(entry.createdAt).toLocaleString()}</Typography></Box>
            ))}
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}
